import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, RefreshCw } from 'lucide-react';
import api, { getQuoteStreamUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './VipDesk.css';

/** HTTP 拉行情间隔（与 WS 并行，兜底）；WS 在线时自动跳过 */
const QUOTE_HTTP_MS = Math.max(200, parseInt(process.env.REACT_APP_QUOTE_HTTP_POLL_MS || '2000', 10));

/** 与 /trades/stock-prices 返回的 key 一致（大写） */
const normSymbol = (s) => String(s || '').trim().toUpperCase();

/** 盘前/盘后 price 可能为 0，退回昨收，避免整卡显示 $0 */
const effectiveQuotePrice = (q) => {
  if (!q || q.error) return NaN;
  const p = q.price != null && q.price !== '' ? parseFloat(q.price) : NaN;
  if (!Number.isNaN(p) && p > 0) return p;
  const pc = q.preClose != null && q.preClose !== '' ? parseFloat(q.preClose) : NaN;
  if (!Number.isNaN(pc) && pc > 0) return pc;
  return NaN;
};

const TIER_FUND = [
  '≥ $100,000',
  '≥ $300,000',
  '≥ $500,000',
  '≥ $1,000,000',
  '≥ $2,000,000',
];

const COMMISSION_ROWS = [
  { level: 'Entry Level Tier', fund: TIER_FUND[0], profit: '30%', commission: '15%' },
  { level: 'Intermediate Tier', fund: TIER_FUND[1], profit: '40%', commission: '15%' },
  { level: 'Advanced Tier', fund: TIER_FUND[2], profit: '50%', commission: '10%' },
  { level: 'Elite Tier', fund: TIER_FUND[3], profit: '80%', commission: '5%' },
  { level: 'Core Tier', fund: TIER_FUND[4], profit: '120%', commission: '5%' },
];

const COMPENSATION_ROWS = [
  { level: 'Entry Level Tier', fund: TIER_FUND[0], risk: '20%', compensation: '5%' },
  { level: 'Intermediate Tier', fund: TIER_FUND[1], risk: '16%', compensation: '7%' },
  { level: 'Advanced Tier', fund: TIER_FUND[2], risk: '13%', compensation: '10%' },
  { level: 'Elite Tier', fund: TIER_FUND[3], risk: '12%', compensation: '11%' },
  { level: 'Core Tier', fund: TIER_FUND[4], risk: '10%', compensation: '15%' },
];

const formatPrice = (n) => {
  const v = Number(n);
  if (Number.isNaN(v) || n == null) return '—';
  return `$ ${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPnl = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRatio = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return `${v.toFixed(2)}%`;
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
};

const SignalCard = ({ signal, live }) => {
  const isClosed = signal.status === 'closed';
  const entry = Number(signal.entryPrice);
  const livePrice = live && Number.isFinite(live.price) ? live.price : NaN;
  const hasLive = !isClosed && livePrice > 0;

  // 持仓中优先用实时价，拉不到才退回库里的 current_price
  const markPrice = isClosed ? signal.exitPrice : (hasLive ? livePrice : signal.currentPrice);

  // 按后端同一公式 (mark - entry) 重算，入场价缺失时沿用入库值
  const useLive = hasLive && entry > 0;
  const pnlAmount = useLive ? (livePrice - entry) * (Number(signal.quantity) || 0) : signal.pnlAmount;
  const pnlRatio = useLive ? ((livePrice - entry) / entry) * 100 : signal.pnlRatio;

  const markLabel = isClosed ? 'Exit:' : 'Current:';
  const positive = Number(pnlAmount) >= 0;

  // 价格变化时整段闪一下；靠 key 变化重新触发动画（首次报价无方向，不闪）
  const flashClass = hasLive && live.dir ? `vipdesk-card__value--flash-${live.dir}` : '';

  return (
    <article className="vipdesk-card">
      <div className="vipdesk-card__head">
        <span className="vipdesk-card__ticker">
          {signal.region || 'USA'} {signal.symbol}
        </span>
      </div>

      <div className="vipdesk-card__grid">
        <div className="vipdesk-card__col">
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">Entry:</span>
            <span className="vipdesk-card__value">{formatPrice(signal.entryPrice)}</span>
          </div>
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">P&amp;L:</span>
            <span className={`vipdesk-card__value ${positive ? 'is-up' : 'is-down'}`}>
              {formatPnl(pnlAmount)}
            </span>
          </div>
        </div>

        <div className="vipdesk-card__col">
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">{markLabel}</span>
            <span
              key={hasLive && live.tickTs ? live.tickTs : 'static'}
              className={`vipdesk-card__value ${flashClass}`}
            >
              {formatPrice(markPrice)}
            </span>
          </div>
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">Ratio:</span>
            <span className={`vipdesk-card__value ${positive ? 'is-up' : 'is-down'}`}>
              {formatRatio(pnlRatio)}
            </span>
          </div>
        </div>

        <div className="vipdesk-card__col">
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">Qty:</span>
            <span className="vipdesk-card__value">{signal.quantity}</span>
          </div>
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">Entry Date:</span>
            <span className="vipdesk-card__value">{formatDate(signal.entryDate)}</span>
          </div>
        </div>
      </div>
    </article>
  );
};

const VipDesk = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [signals, setSignals] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [livePrices, setLivePrices] = useState({});
  const quoteWsRef = useRef(null);

  useEffect(() => {
    // Wait for AuthContext to finish checking localStorage token
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/vip-desk' } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/vip-signals', { params: { category: 'trading' } });
      if (res.data.success) {
        setSignals(res.data.data.signals || []);
        setMeta(res.data.data.meta?.trading || null);
      } else {
        setError(res.data.message || 'Failed to load');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Failed to load signals';
      setError(msg);
      if (e.response?.status === 401) {
        navigate('/login', { state: { from: '/vip-desk' } });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) {
      load();
    }
  }, [authLoading, isAuthenticated]);

  const activeSymbols = useMemo(
    () => [
      ...new Set(
        signals
          .filter((s) => s.status !== 'closed')
          .map((s) => normSymbol(s.symbol))
          .filter(Boolean)
      ),
    ],
    [signals]
  );

  useEffect(() => {
    if (activeSymbols.length === 0) {
      setLivePrices({});
      return undefined;
    }

    let stopped = false;
    let reconnectTimer;

    const applyQuote = (sym, q) => {
      const px = effectiveQuotePrice(q);
      if (Number.isNaN(px)) return;
      setLivePrices((prev) => {
        const cur = prev[sym];
        if (cur && cur.price === px) return prev;
        const dir = cur && Number.isFinite(cur.price) ? (px > cur.price ? 'up' : 'down') : null;
        return { ...prev, [sym]: { price: px, tickTs: Date.now(), dir } };
      });
    };

    const fetchHttp = async () => {
      try {
        const res = await api.post('/trades/stock-prices', { symbols: activeSymbols });
        if (stopped || !res.data?.success || !res.data?.data) return;
        for (const key of Object.keys(res.data.data)) {
          applyQuote(normSymbol(key), res.data.data[key]);
        }
      } catch (_) {
        /* 拉取失败时保留上一次报价 */
      }
    };

    fetchHttp();

    const connectWs = () => {
      if (stopped) return;
      try {
        const ws = new WebSocket(getQuoteStreamUrl());
        quoteWsRef.current = ws;
        ws.onopen = () => {
          ws.send(JSON.stringify({ action: 'subscribe', symbols: activeSymbols }));
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === 'quote' && msg.data && msg.data.symbol) {
              applyQuote(normSymbol(msg.data.symbol), msg.data);
            }
          } catch (e) {
            console.warn('Quote WS message parse', e);
          }
        };
        ws.onclose = () => {
          if (stopped) return;
          quoteWsRef.current = null;
          reconnectTimer = setTimeout(connectWs, 3000);
        };
        ws.onerror = () => {
          try {
            ws.close();
          } catch (_) {
            /* noop */
          }
        };
      } catch (e) {
        console.error('Quote WebSocket', e);
        reconnectTimer = setTimeout(connectWs, 5000);
      }
    };

    connectWs();

    const isWsAlive = () => quoteWsRef.current && quoteWsRef.current.readyState === WebSocket.OPEN;
    const httpFallback = setInterval(() => {
      if (stopped) return;
      if (document.hidden) return;
      if (isWsAlive()) return;
      fetchHttp();
    }, QUOTE_HTTP_MS);

    return () => {
      stopped = true;
      clearInterval(httpFallback);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (quoteWsRef.current) {
        quoteWsRef.current.close();
        quoteWsRef.current = null;
      }
    };
  }, [activeSymbols.join('|')]);

  const header = meta || {
    title: 'VIP Trading Strategy',
    description: 'Once you have access to the VIP trading strategy please do not disclose any information',
  };

  if (authLoading) {
    return (
      <div className="vipdesk-page">
        <div className="vipdesk-locked">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="vipdesk-page">
        <div className="vipdesk-locked">
          <Lock size={28} />
          <p>Please sign in to continue</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vipdesk-page">
      <div className="vipdesk-wrap">
        <div className="vipdesk-toolbar">
          <button type="button" className="vipdesk-refresh" onClick={load} title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>

        <section className="vipdesk-header-card">
          <div className="vipdesk-header-card__top">
            <h1 className="vipdesk-header-card__title">{header.title}</h1>
          </div>
          <p className="vipdesk-header-card__desc">{header.description}</p>
        </section>

        {loading && <div className="vipdesk-empty">Loading signals…</div>}
        {!loading && error && <div className="vipdesk-empty vipdesk-empty--error">{error}</div>}
        {!loading && !error && signals.length === 0 && (
          <div className="vipdesk-empty">No signals yet. Check back soon.</div>
        )}

        <div className="vipdesk-list">
          {signals.map((s) => (
            <SignalCard key={s.id} signal={s} live={livePrices[normSymbol(s.symbol)]} />
          ))}
        </div>

        {/* Membership Agreement / Commission tables */}
        <section className="vipdesk-agreement">
          <h2 className="vipdesk-agreement__title">Membership Agreement</h2>
          <p className="vipdesk-agreement__intro">
            We provide professional trading strategies to VIP club members. We charge a commission
            based on a percentage of the profits generated by the strategies, while also
            compensating for any losses incurred.
          </p>

          <h3 className="vipdesk-agreement__section-title">Membership Commission Mechanism</h3>
          <div className="vipdesk-table-wrap">
            <table className="vipdesk-table">
              <thead>
                <tr>
                  <th>Membership Level</th>
                  <th>Fund Range</th>
                  <th>Monthly Profit Ratio</th>
                  <th>Commission Ratio</th>
                </tr>
              </thead>
              <tbody>
                {COMMISSION_ROWS.map((row) => (
                  <tr key={row.level}>
                    <td>
                      <span className="vipdesk-tier-pill">{row.level}</span>
                    </td>
                    <td className="vipdesk-table__fund">{row.fund}</td>
                    <td className="vipdesk-table__green">{row.profit}</td>
                    <td className="vipdesk-table__green">{row.commission}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="vipdesk-agreement__section-title">Membership Compensation Mechanism</h3>
          <div className="vipdesk-table-wrap">
            <table className="vipdesk-table">
              <thead>
                <tr>
                  <th>Membership Level</th>
                  <th>Fund Range</th>
                  <th>Risk Ratio</th>
                  <th>Compensation Ratio</th>
                </tr>
              </thead>
              <tbody>
                {COMPENSATION_ROWS.map((row) => (
                  <tr key={row.level}>
                    <td>
                      <span className="vipdesk-tier-pill">{row.level}</span>
                    </td>
                    <td className="vipdesk-table__fund">{row.fund}</td>
                    <td className="vipdesk-table__green">{row.risk}</td>
                    <td className="vipdesk-table__green">{row.compensation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* View Full Agreement button — temporarily hidden */}
        </section>
      </div>
    </div>
  );
};

export default VipDesk;
